import { WarningIcon } from "@phosphor-icons/react/Warning";
import ProgressSpinner from "@/Shared/Icons/ProgressSpinner";
import { ProgressSpinnerSizes } from "@/Shared/Icons/ProgressSpinnerSizes";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { copyToClipboard } from "@freightsimple/ui-core/Feedback/copyToClipboard";
import { Table, type Column } from "@freightsimple/ui-core/Table/Table";
import type { Invitation } from "@freightsimple/generated-dashboard-openapi-client/models/Invitation";
import { isDateInThePast } from "@/Helpers/isDateInThePast";
import moment from "moment";
import { useState } from "react";

interface CopyLinkButtonProps {
  link: string | undefined;
}

// copyToClipboard raises the toast itself — success or failure — which is why
// this doesn't hold a "copied" flag of its own. A flag can't say "the write
// failed" (false is also its initial state) and it can't toast twice in a row
// without changing value, and both were how copying stayed silent here.
function CopyLinkButton_(props: CopyLinkButtonProps) {
  const link = props.link;

  if (link === undefined) {
    return <></>;
  }

  return (
    <Button
      size="link"
      onClick={() => copyToClipboard(link, "Invitation link")}
      data-testid="copy-invitation-link-button"
    >
      Copy Link
    </Button>
  );
}

interface InvitationsSectionProps {
  invitations: Invitation[];
  /** Resend the invitation for this email. Owned by the screen. */
  onResend: (inviteeEmailAddress: string) => Promise<void>;
  /** Ask to revoke the invitation for this email. Owned by the screen. */
  onRevoke: (inviteeEmailAddress: string) => void;
}

// Presentational: the revoke-confirm and result-notice dialogs live on
// ViewCompanyScreen, because revoking the last invitation unmounts this
// whole section (the screen only renders it when invitations exist) — a
// dialog owned here would vanish mid-flow.
function ViewCompanyInvitationsSection(props: InvitationsSectionProps) {
  const { invitations } = props;

  function formatDate(date: Date): string {
    if (moment(date).diff(moment.now(), "days") < 7) {
      return moment(date).fromNow();
    } else {
      return moment(date).format("dddd Do MMMM YYYY h:mma z");
    }
  }

  const invitationColumns: Column<Invitation>[] = [
    {
      key: "email",
      header: "Email",
      render: (i) => {
        const isInvitationExpired = isDateInThePast(i.expiresAt?.toISOString());
        const delta = moment(i.expiresAt).fromNow();
        return (
          <div className="flex items-center gap-2">
            {isInvitationExpired && (
              <>
                <WarningIcon className="h-5 w-5 text-orange" weight="fill" />
                <span className="mr-2 font-medium text-orange">
                  Expired {delta}
                </span>
              </>
            )}
            {i.email}
          </div>
        );
      },
    },
    { key: "name", header: "Name", render: (i) => i.name },
    {
      key: "lastSent",
      header: "Last sent",
      render: (i) => formatDate(i.lastSent),
    },
  ];

  return (
    <>
      <h2 className="text-2xl font-bold text-foreground">Invitations</h2>
      <div className="h-[16px]" />
      <Table
        tableId="invitations"
        columns={invitationColumns}
        data={invitations}
        rowKey={(i) => i.email}
        emptyMessage="No pending invitations"
        rowActions={(i) => {
          const isInvitationExpired = isDateInThePast(
            i.expiresAt?.toISOString(),
          );
          return (
            <>
              {!isInvitationExpired && <CopyLinkButton_ link={i.link} />}
              <ResendButton_ onClick={() => props.onResend(i.email)} />
              <Button size="link" onClick={() => props.onRevoke(i.email)}>
                Revoke
              </Button>
            </>
          );
        }}
      />
    </>
  );
}

function ResendButton_({ onClick }: { onClick: () => Promise<void> }) {
  const [loading, setLoading] = useState(false);

  async function handleClick() {
    setLoading(true);
    await onClick();
    setLoading(false);
  }

  return (
    <Button
      size="link"
      disabled={loading}
      disabledMessage="Resending..."
      onClick={handleClick}
    >
      Resend
      {loading && <ProgressSpinner size={ProgressSpinnerSizes.Small} />}
    </Button>
  );
}

export default ViewCompanyInvitationsSection;
